import { registerQuestionnaireResponse } from "../slices/questionnaireResponseSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetch a QuestionnaireResponse by ID
 * - Checks Redux store first
 * - Fetches from FHIR server if missing
 * - Registers into Redux
 */
export const fetchQuestionnaireResponse = async (
    questionnaireResponseId,
    fhirClient,
    store,
    { forceFetch = false } = {}
) => {
    if (!questionnaireResponseId || !fhirClient) return null;

    // 🧠 Check Redux store
    const cached = store.getState().questionnaireResponses?.[questionnaireResponseId];
    if (cached && !forceFetch) {
        return cached;
    }

    try {
        // 🌐 Fetch from FHIR
        const fetched = await fhirClient.request(
            `QuestionnaireResponse/${questionnaireResponseId}`
        );

        if (fetched?.resourceType !== "QuestionnaireResponse") {
            console.warn(
                "⚠️ Unexpected QuestionnaireResponse response:",
                fetched
            );
            return null;
        }

        store.dispatch(registerQuestionnaireResponse(fetched));
        return fetched;

    } catch (err) {
        console.error("❌ Failed to fetch QuestionnaireResponse:", err);

        const status = err?.response?.status || err?.status || 500;

        logErrorEvent({
            errorType: "read",
            error: {
                message: err?.message || "QuestionnaireResponse fetch failed",
                status,
                resourceId: `QuestionnaireResponse/${questionnaireResponseId}`
            },
            description: `Failed to retrieve QuestionnaireResponse/${questionnaireResponseId} from FHIR server`,
            store
        });

        return { error: status };
    }
};

/**
 * Fetch a QuestionnaireResponse given a FHIR reference string
 * e.g. "QuestionnaireResponse/123" or "QuestionnaireResponse/123/_history/4"
 */
export const fetchQuestionnaireResponseByReference = async (
    ref,
    fhirClient,
    store,
    options = {}
) => {
    if (!ref || typeof ref !== "string") {
        console.warn(
            "⚠️ Invalid QuestionnaireResponse reference (not a string):",
            ref
        );
        return null;
    }

    if (!ref.startsWith("QuestionnaireResponse/")) {
        console.warn("⚠️ Reference is not a QuestionnaireResponse:", ref);
        return null;
    }

    // Extract ID safely (handles versioned refs)
    const parts = ref.split("/");
    const questionnaireResponseId = parts[1];

    if (
        !questionnaireResponseId ||
        questionnaireResponseId === "undefined" ||
        questionnaireResponseId === "null"
    ) {
        console.warn(
            "⚠️ Could not extract valid QuestionnaireResponse ID from reference:",
            ref
        );
        return null;
    }

    return fetchQuestionnaireResponse(
        questionnaireResponseId,
        fhirClient,
        store,
        options
    );
};
